import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import type { MonitoringCheck } from '@bekebi/contracts/api';
import { Button } from '../../components/button';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { isServiceId, useServices } from '../services/api';
import { DefinitionList, Section, UnavailableNote } from '../services/components';
import { CHECK_KIND_TITLES, formatDateTime } from '../services/format';
import { EditCheckForm } from './check-form';
import { useCheck, useCheckResults, useUpdateCheck } from './api';
import {
  NEXT_RESULT_NOTE,
  CheckStateBadge,
  ResultDetails,
  ResultStatus,
  STALE_EXPLANATION,
  formatInterval,
  targetSummary,
} from './present';

/**
 * One check (/app/monitoring/:checkId): its configuration, its current state as stored by the
 * worker (including the API's stale flag), enable/disable and editing (monitoring.manage), and its
 * result history, newest first. Every value is read from the API.
 */
export function CheckDetail() {
  const { checkId } = useParams();
  if (!isServiceId(checkId)) return <CheckNotFound />;
  return <CheckDetailFor id={checkId} />;
}

function CheckNotFound() {
  return (
    <div className="max-w-3xl">
      <h1>Check not found</h1>
      <p className="mt-2 text-sm text-muted">There is no monitoring check at this address.</p>
      <p className="mt-4 text-sm">
        <Link
          to="/app/monitoring"
          className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
        >
          Back to Monitoring
        </Link>
      </p>
    </div>
  );
}

function CheckDetailFor({ id }: { id: string }) {
  const check = useCheck(id);
  if (check.isPending) return <LoadingState label="Loading check…" />;
  if (check.isError) {
    return (
      <ErrorState
        title="The check could not be loaded"
        message={check.error.message}
        onRetry={() => void check.refetch()}
      />
    );
  }
  if (!check.data) return <CheckNotFound />;
  return <CheckView check={check.data} />;
}

/** What each kind of check does and does not prove. */
const SCOPE: Record<MonitoringCheck['kind'], string> = {
  website_https:
    'Website availability over HTTPS: status, response time, redirects and TLS certificate expiry. Checked from the server, never from your browser.',
  nextcloud_status:
    'Application health: whether the Nextcloud application answers its status.php correctly (installed, not in maintenance, no pending upgrade). This is not server or host health.',
  smtp_tls:
    'Protocol availability: whether the SMTP service accepts a verified TLS connection and greets correctly. A passing check does not prove that email is delivered.',
  imap_tls:
    'Protocol availability: whether the IMAP service accepts a verified TLS connection and greets correctly. A passing check does not prove that email is delivered or can be read.',
  mail_dns:
    'Mail domain DNS: whether MX, SPF, DMARC and DKIM records are published and the mail server’s reverse DNS matches its name — what receiving providers check before accepting mail. Public DNS lookups only; a passing check does not prove that email is delivered.',
};

function CheckView({ check }: { check: MonitoringCheck }) {
  const canManage = useHasPermission('monitoring.manage');
  const services = useServices();
  const service = services.data?.find((s) => s.id === check.serviceId);
  const location = useLocation();
  const created = (location.state as { created?: boolean } | null)?.created;

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <p className="text-sm">
          <Link
            to="/app/monitoring"
            className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
          >
            Monitoring
          </Link>
        </p>
        <h1 className="mt-2">{CHECK_KIND_TITLES[check.kind]}</h1>
        <p className="mt-1 text-sm text-muted">
          Service:{' '}
          {service ? (
            <Link
              to={`/app/services/${service.id}`}
              className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
            >
              {service.name}
            </Link>
          ) : (
            'not loaded'
          )}
        </p>
        {created ? (
          <p role="status" className="mt-4 rounded-md bg-success-soft p-3 text-sm text-success">
            The check was added.
          </p>
        ) : null}
      </div>

      <Section title="Current state" action={canManage ? <EnableToggle check={check} /> : null}>
        <div className="space-y-2 text-sm">
          <CheckStateBadge check={check} />
          <p className="text-muted">
            Last checked:{' '}
            {check.lastCheckedAt ? formatDateTime(check.lastCheckedAt) : 'No result yet'}
          </p>
          {check.enabled && check.lastStatus !== null && check.stale ? (
            <p className="text-warning">{STALE_EXPLANATION}</p>
          ) : null}
          {!check.enabled ? (
            <p className="text-muted">
              This check is disabled: it does not run and does not count towards the service&rsquo;s
              health.
            </p>
          ) : null}
        </div>
      </Section>

      <Section title="What this checks" description={SCOPE[check.kind]}>
        <DefinitionList
          items={[
            [
              'Target',
              <span key="t" className="break-all font-mono">
                {targetSummary(check)}
              </span>,
            ],
            ['Interval', formatInterval(check.intervalSeconds)],
            ['Enabled', check.enabled ? 'Yes' : 'No'],
            ['Added', formatDateTime(check.createdAt)],
            ['Last changed', formatDateTime(check.updatedAt)],
          ]}
        />
        <p className="mt-4 text-sm text-muted">{check.description}</p>
      </Section>

      <ResultsSection check={check} />

      {canManage ? (
        <Section title="Edit">
          <EditCheckForm check={check} />
        </Section>
      ) : null}
    </div>
  );
}

function EnableToggle({ check }: { check: MonitoringCheck }) {
  const update = useUpdateCheck(check.id);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="text-right">
      <Button
        variant="secondary"
        disabled={update.isPending}
        onClick={() => {
          setError(null);
          update.mutate({ enabled: !check.enabled }, { onError: (e) => setError(e.message) });
        }}
      >
        {update.isPending ? 'Saving…' : check.enabled ? 'Disable check' : 'Enable check'}
      </Button>
      {!check.enabled ? (
        <p className="mt-1 max-w-56 text-xs text-muted">
          Enabling clears the last result. {NEXT_RESULT_NOTE}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-1 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ResultsSection({ check }: { check: MonitoringCheck }) {
  const results = useCheckResults(check.id);
  const items = results.data?.pages.flatMap((p) => p.items) ?? [];
  const [latest, ...older] = items;

  return (
    <>
      <Section title="Latest result">
        {results.isPending ? (
          <LoadingState label="Loading results…" />
        ) : results.isError ? (
          <ErrorState
            title="Results could not be loaded"
            message={results.error.message}
            onRetry={() => void results.refetch()}
          />
        ) : !latest ? (
          <UnavailableNote>No result yet.</UnavailableNote>
        ) : (
          <div className="space-y-3">
            <ResultStatus status={latest.status} />
            {!check.enabled ? (
              <p className="text-sm text-muted">
                The check is disabled, so this result is not current and does not count towards the
                service&rsquo;s health.
              </p>
            ) : check.stale ? (
              <p className="text-sm text-warning">
                This is the latest recorded result, but it is stale, so it is not treated as
                current.
              </p>
            ) : null}
            <ResultDetails result={latest} />
          </div>
        )}
      </Section>

      {older.length > 0 || results.hasNextPage ? (
        <Section title="Earlier results" description="Newest first, as recorded.">
          <ul aria-label="Earlier results" className="divide-y divide-line">
            {older.map((result) => (
              <li key={result.id} className="py-2 text-sm">
                <details>
                  <summary className="flex min-h-10 cursor-pointer flex-wrap items-center gap-3">
                    <ResultStatus status={result.status} />
                    <span className="text-fg-2">{formatDateTime(result.checkedAt)}</span>
                    {result.errorMessage ? (
                      <span className="text-muted">{result.errorMessage}</span>
                    ) : null}
                  </summary>
                  <div className="mt-2 pl-2">
                    <ResultDetails result={result} />
                  </div>
                </details>
              </li>
            ))}
          </ul>
          {results.hasNextPage ? (
            <div className="mt-3">
              <Button
                variant="secondary"
                onClick={() => void results.fetchNextPage()}
                disabled={results.isFetchingNextPage}
              >
                {results.isFetchingNextPage ? 'Loading…' : 'Show older results'}
              </Button>
            </div>
          ) : null}
        </Section>
      ) : null}
    </>
  );
}
